import React, { useEffect, useMemo, useState } from "react";
import Utils from "../../Utils";
import Auth from "../../Auth";
import { useFormikContext } from "formik";
import { useTestResultReducer } from "./TestResultReducer";
import InlineSVG from "react-inlinesvg";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Link } from "react-router-dom";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { getLiquidReferencedVariables, normalizeReferencedVariable } from "../../../../src/shared/schemas/PluginSchema";
import { PLUGIN_TESTING_UNAVAILABLE_MESSAGE } from "../../../../src/shared/constants";
import { countWords } from "../../Utils";
import { ContextInputsSection } from "./PluginTesterSections/ContextInputsSection";
import { ExampleInputSection } from "./PluginTesterSections/ExampleInputSection";
import { StoryBibleInputsSection } from "./PluginTesterSections/StoryBibleInputsSection";
import {
  DEFAULT_STORY_BIBLE,
  SAMPLE_TEST_PASSAGE,
  STORY_BIBLE_LABELS,
} from "./PluginTesterSections/constants";

function userInputTextValues(formikContextValues) {
  const block = formikContextValues?.blocks?.find((b) => b.blockType === "input_document");
  if (!block) return null;

  const output = block?.options?.outputs?.find((o) => o.source === "user_text_input");
  if (!output) return null;

  return output;
}

function getInputDocumentBlock(formikContextValues) {
  return formikContextValues?.blocks?.find((b) => b.blockType === "input_document");
}

function validateInput(text, minWords, maxWords, inputName) {
  const wordCount = countWords(text);

  if (minWords && wordCount < minWords) {
    return `${inputName}: Select at least ${minWords} words. (Currently ${wordCount} words)`;
  }

  if (maxWords && wordCount > maxWords) {
    return `${inputName}: Too many words selected. Select fewer than ${maxWords} words. (Currently ${wordCount} words)`;
  }

  return null;
}

export const PluginTester = () => {
  const [testValue, setTestValue] = useState("");
  const [precedingTextValue, setPrecedingTextValue] = useState("");
  const [userTextInputValue, setUserTextInputValue] = useState("");
  const [previousDocumentTextValue, setPreviousDocumentTextValue] = useState("");
  const [followingTextValue, setFollowingTextValue] = useState("");
  const [testResult, dispatchTestResult] = useTestResultReducer();
  const [isRunning, setIsRunning] = useState(false);
  const { values } = useFormikContext();
  const [storyBible, setStoryBible] = useState(DEFAULT_STORY_BIBLE);
  const [creditCost, setCreditCost] = useState(null);
  const userInputText = userInputTextValues(values);
  const [showStoryBible, setShowStoryBible] = useState(false);
  const [collapsedStoryBibleSections, setCollapsedStoryBibleSections] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);

  const defaultCollapsedStoryBibleSections = useMemo(
    () =>
      Object.keys(DEFAULT_STORY_BIBLE).reduce((acc, fieldName) => {
        acc[fieldName] = true;
        return acc;
      }, {}),
    []
  );

  const promptVariableSet = useMemo(() => {
    const variables = new Set();
    const inferenceBlocks = values?.blocks?.filter((b) => b.blockType === "inference") || [];
    for (const block of inferenceBlocks) {
      const messages = block?.options?.prompt?.messages || [];
      for (const message of messages) {
        const template = message?.template || "";
        if (!template) continue;
        const messageVariables = getLiquidReferencedVariables(template) || [];
        messageVariables.forEach((variableName) => {
          const normalizedVariableName = normalizeReferencedVariable(variableName);
          variables.add(normalizedVariableName);
        });
      }
    }
    return variables;
  }, [values?.blocks]);

  const shouldShowPrecedingText = promptVariableSet.has("preceding_text");
  const shouldShowFollowingText = promptVariableSet.has("following_text");
  const shouldShowPreviousDocumentText = promptVariableSet.has("previous_document_text");
  const shouldShowHighlightedText = promptVariableSet.has("highlighted_text");

  useEffect(() => {
    if (showStoryBible) {
      setCollapsedStoryBibleSections(defaultCollapsedStoryBibleSections);
    }
  }, [showStoryBible, defaultCollapsedStoryBibleSections]);

  const handleRunTest = async () => {
    if (isRunning) {
      return;
    }

    const errors = [];
    const inputDocBlock = getInputDocumentBlock(values);

    if (inputDocBlock?.options?.outputs) {
      for (const outputConfig of inputDocBlock.options.outputs) {
        const { source, minWords, maxWords } = outputConfig;

        if (source === "highlighted_text" && shouldShowHighlightedText && testValue) {
          const error = validateInput(testValue, minWords, maxWords, "Selected Text");
          if (error) errors.push(error);
        } else if (source === "preceding_text" && precedingTextValue) {
          const error = validateInput(precedingTextValue, minWords, maxWords, "Preceding Text");
          if (error) errors.push(error);
        } else if (source === "following_text" && followingTextValue) {
          const error = validateInput(followingTextValue, minWords, maxWords, "Following Text");
          if (error) errors.push(error);
        } else if (source === "previous_document_text" && previousDocumentTextValue) {
          const error = validateInput(previousDocumentTextValue, minWords, maxWords, "Previous Document Text");
          if (error) errors.push(error);
        } else if (source === "user_text_input" && userTextInputValue) {
          const error = validateInput(userTextInputValue, minWords, maxWords, "User Text Input");
          if (error) errors.push(error);
        }
      }
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);
    dispatchTestResult({ type: "reset" });
    setIsRunning(true);
    setCreditCost(null);
    const params = {
      method: "POST",
      highlighted_text: shouldShowHighlightedText ? testValue : "",
      preceding_text: precedingTextValue,
      following_text: followingTextValue,
      previous_document_text: previousDocumentTextValue,
      user_text_input: userTextInputValue,
      test: true,
      plugin: values,
      storyBible,
    };

    Auth.streamWithIdToken(
      "/api/ai/testPluginAction",
      params,
      null,
      handleChunk,
      () => {
        setIsRunning(false);
      },
      (error) => {
        setIsRunning(false);
        handleError(error);
      }
    );
  };

  const handleChunk = (chunk) => {
    if (chunk?.prompt) {
      dispatchTestResult({ type: "prompt", payload: chunk });
    }
    if (chunk?.content?.text) {
      dispatchTestResult({ type: "textChunk", payload: chunk });
    }

    if (chunk.cost) {
      setCreditCost(chunk.cost);
    }
  };

  const handleError = (error) => {
    dispatchTestResult({ type: "error", payload: error });
  };

  const updateStoryBibleContent = (fieldName) => (e) => {
    setStoryBible({ ...storyBible, [fieldName]: e.target.value });
  };

  const toggleStoryBibleSection = (fieldName) => {
    setCollapsedStoryBibleSections((previous) => ({
      ...previous,
      [fieldName]: !previous[fieldName],
    }));
  };

  const isStoryBibleSectionCollapsed = (fieldName) =>
    collapsedStoryBibleSections[fieldName] ?? true;

  const insertExamplePassage = () => {
    setTestValue((previousValue) => {
      const trimmedValue = previousValue.trim();
      if (!trimmedValue.length) {
        return SAMPLE_TEST_PASSAGE;
      }

      return `${trimmedValue}\n\n${SAMPLE_TEST_PASSAGE}`;
    });
  };

  const getStoryBibleLabel = (fieldName) => {
    if (STORY_BIBLE_LABELS[fieldName]) {
      return STORY_BIBLE_LABELS[fieldName];
    }

    return Utils.capitalize(fieldName.replace(/_/g, " "));
  };

  if (subscriptionJSON?.hasAccessToPluginTesting === false) {
    return (
      <div className="testing-area">
        <p className="pluginTestingUnavailableMessage">
          {PLUGIN_TESTING_UNAVAILABLE_MESSAGE} <Link to="/app/plans?ref=plugin-test">Subscribe to Sudowrite</Link>.
        </p>
      </div>
    );
  }

  const inputDocBlock = getInputDocumentBlock(values);
  const getWordLimits = (source) => {
    const outputConfig = inputDocBlock?.options?.outputs?.find((o) => o.source === source);
    return outputConfig ? { minWords: outputConfig.minWords, maxWords: outputConfig.maxWords } : {};
  };

  const highlightedTextLimits = getWordLimits("highlighted_text");
  const precedingTextLimits = getWordLimits("preceding_text");
  const followingTextLimits = getWordLimits("following_text");
  const previousDocTextLimits = getWordLimits("previous_document_text");
  const userTextInputLimits = getWordLimits("user_text_input");

  return (
    <div className="testing-area">
      {shouldShowHighlightedText && (
        <ExampleInputSection
          testValue={testValue}
          onTestValueChange={setTestValue}
          onInsertExample={insertExamplePassage}
          highlightedTextLimits={highlightedTextLimits}
        />
      )}

      <ContextInputsSection
        userInputText={userInputText}
        userTextInputValue={userTextInputValue}
        onUserTextInputChange={setUserTextInputValue}
        userTextInputLimits={userTextInputLimits}
        shouldShowPrecedingText={shouldShowPrecedingText}
        precedingTextValue={precedingTextValue}
        onPrecedingTextChange={setPrecedingTextValue}
        precedingTextLimits={precedingTextLimits}
        shouldShowFollowingText={shouldShowFollowingText}
        followingTextValue={followingTextValue}
        onFollowingTextChange={setFollowingTextValue}
        followingTextLimits={followingTextLimits}
        shouldShowPreviousDocumentText={shouldShowPreviousDocumentText}
        previousDocumentTextValue={previousDocumentTextValue}
        onPreviousDocumentTextChange={setPreviousDocumentTextValue}
        previousDocTextLimits={previousDocTextLimits}
      />

      <StoryBibleInputsSection
        showStoryBible={showStoryBible}
        onToggleStoryBible={setShowStoryBible}
        storyBible={storyBible}
        isStoryBibleSectionCollapsed={isStoryBibleSectionCollapsed}
        onToggleStoryBibleSection={toggleStoryBibleSection}
        onUpdateStoryBibleContent={updateStoryBibleContent}
        getStoryBibleLabel={getStoryBibleLabel}
      />

      <div className="testerRunButtonRow">
        <SudoButton
          variant="secondary"
          type="button"
          leftIcon="/assets/AI-sparkle.svg"
          state={isRunning ? "loading" : undefined}
          disabled={isRunning}
          onClick={handleRunTest}
        >
          Run Test
        </SudoButton>
      </div>

      {validationErrors.length > 0 && (
        <div
          className="error testerValidationErrors"
        >
          <h3>Validation Errors:</h3>
          <ul className="testerValidationErrorsList">
            {validationErrors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {testResult.length > 0 && (
        <div className="test-result-container">
          <h3>Test Result: </h3>
          {testResult.map((responseBlock, i) => {
            if (responseBlock.type === "prompt") {
              return (
                <div
                  key={`response-${i}`}
                  className="test-response"
                >
                  <h4>{responseBlock.title}</h4>
                  {responseBlock.content.map((message, j) => (
                    <div key={`message-${j}`}>{Utils.nl2p(message.content)}</div>
                  ))}
                </div>
              );
            } else if (responseBlock.type === "error") {
              return (
                <div
                  key={`response-${i}`}
                  className="error"
                >
                  <h4>Error</h4>
                  <p>{responseBlock.content}</p>
                </div>
              );
            }

            return (
              <div
                key={`response-${i}`}
                className="test-response"
              >
                <h4>{responseBlock.isForEndUser ? "Card Output" : responseBlock.title}</h4>
                {Utils.nl2p(responseBlock.content)}
              </div>
            );
          })}
          {creditCost && (
            <div className="credits-display">
              <p>Credits used: </p>
              <span className="credit-amount">
                <span>{creditCost}</span>
                <InlineSVG
                  aria-label="credits"
                  src="/assets/pricing/credits.svg"
                />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
